import { PromptTemplateBlock, PromptTemplateBlockTypeEnum, PromptTemplateStructureEnum } from '../types';

export const validateJson = (value: string): boolean => {
    if (!value.trim()) {
        return true;
    }
    try {
        JSON.parse(value);
        return true;
    } catch {
        return false;
    }
};

export const addBlock = (blocks: PromptTemplateBlock[], collapsedKeys: string[]) => {
    const newBlocks = [
        ...blocks,
        {
            title: `Block ${blocks.length + 1}`,
            comment: '',
            length: 0,
            body: '',
            blockType: PromptTemplateBlockTypeEnum.Plain
        }
    ];

    const newBlockKey = `block-${newBlocks.length - 1}`;
    const newCollapsedKeys = [...collapsedKeys, newBlockKey];

    return { blocks: newBlocks, collapsedKeys: newCollapsedKeys };
};

export const updateBlock = (
    blocks: PromptTemplateBlock[],
    index: number,
    field: keyof PromptTemplateBlock,
    value: string
): PromptTemplateBlock[] => {
    const newBlocks = [...blocks];
    newBlocks[index] = {
        ...newBlocks[index],
        [field]: value
    };
    return newBlocks;
};

export const removeBlock = (blocks: PromptTemplateBlock[], index: number): PromptTemplateBlock[] => {
    if (blocks.length > 1) {
        return blocks.filter((_, i) => i !== index);
    }
    return blocks;
};

export const reorderBlocks = (
    blocks: PromptTemplateBlock[],
    dragIndex: number,
    hoverIndex: number
): PromptTemplateBlock[] => {
    const newBlocks = [...blocks];
    const dragBlock = newBlocks[dragIndex];
    newBlocks.splice(dragIndex, 1);
    newBlocks.splice(hoverIndex, 0, dragBlock);
    return newBlocks;
};

export const updateCollapsedKeys = (key: string | string[]): string[] => {
    return Array.isArray(key) ? key : [key];
};

export const expandAllBlocks = (blocks: PromptTemplateBlock[]): string[] => {
    return blocks.map((_, index) => `block-${index}`);
};

export const areAllExpanded = (blocks: PromptTemplateBlock[], collapsedKeys: string[]): boolean => {
    return blocks.length > 0 && collapsedKeys.length === blocks.length;
};

export const calculateWordCount = (blocks: PromptTemplateBlock[]): number => {
    return blocks
        .map(block => block.body || '')
        .join('')
        .trim()
        .split(/\s+/)
        .filter(word => word.length > 0).length;
};

export const concatenateBlockBodies = (blocks: PromptTemplateBlock[]): string => {
    return blocks.map(block => block.body || '').join('\n');
};

export const prepareBlocksForSubmission = (blocks: PromptTemplateBlock[]): PromptTemplateBlock[] => {
    return blocks.map(block => ({
        id: block.id,
        title: block.title,
        comment: block.comment,
        body: block.body,
        blockType: block.blockType
    }));
};

export const initializeBlocksFromTemplate = (template: {
    body?: string;
    blocks?: Partial<PromptTemplateBlock>[];
}): { blocks: PromptTemplateBlock[]; collapsedKeys: string[] } => {
    let initialBlocks: PromptTemplateBlock[] = [];

    if (template.blocks && Array.isArray(template.blocks) && template.blocks.length > 0) {
        initialBlocks = template.blocks.map((block: Partial<PromptTemplateBlock>, index: number) => ({
            id: block.id,
            title: block.title || `Block ${index + 1}`,
            comment: block.comment || '',
            body: block.body || '',
            blockType: block.blockType || PromptTemplateBlockTypeEnum.Plain
        }));
    } else {
        initialBlocks = [
            {
                title: 'Block 1',
                comment: '',
                body: template.body || '',
                blockType: PromptTemplateBlockTypeEnum.Plain
            }
        ];
    }

    const collapsedKeys = initialBlocks.map((_, index) => `block-${index}`);

    return { blocks: initialBlocks, collapsedKeys };
};

// Validation interface for orchestrated template block rules
export interface OrchestrationValidationResult {
    isValid: boolean;
    errors: string[];
}

// Validate block type distribution for Orchestrated templates
export const validateOrchestratedBlocks = (
    blocks: PromptTemplateBlock[],
    templateStructure: PromptTemplateStructureEnum
): OrchestrationValidationResult => {
    // If template is not Orchestrated, no validation needed
    if (templateStructure !== PromptTemplateStructureEnum.Orchestrated) {
        return { isValid: true, errors: [] };
    }

    const errors: string[] = [];

    // Count blocks by type
    const blockCounts = {
        plain: 0,
        preprocessing: 0,
        processing: 0,
        postprocessing: 0
    };

    blocks.forEach(block => {
        switch (block.blockType) {
            case PromptTemplateBlockTypeEnum.Plain:
                blockCounts.plain++;
                break;
            case PromptTemplateBlockTypeEnum.Preprocessing:
                blockCounts.preprocessing++;
                break;
            case PromptTemplateBlockTypeEnum.Processing:
                blockCounts.processing++;
                break;
            case PromptTemplateBlockTypeEnum.Postprocessing:
                blockCounts.postprocessing++;
                break;
            default:
                break;
        }
    });

    // Validate Plain blocks: not allowed in Orchestrated templates
    if (blockCounts.plain > 0) {
        errors.push(
            `Plain blocks: Found ${blockCounts.plain}, but Plain blocks are not allowed in Orchestrated templates. Please assign a valid block type.`
        );
    }

    // Validate Pre-Processing: optional; 0 or 1 allowed
    if (blockCounts.preprocessing > 1) {
        errors.push(`Pre-Processing blocks: Found ${blockCounts.preprocessing}, but only 0 or 1 is allowed.`);
    }

    // Validate Processing: ≥ 1 required
    if (blockCounts.processing < 1) {
        errors.push(`Processing blocks: Found ${blockCounts.processing}, but at least 1 is required.`);
    }

    // Validate Post-Processing: exactly 1 required
    if (blockCounts.postprocessing !== 1) {
        errors.push(`Post-Processing blocks: Found ${blockCounts.postprocessing}, but exactly 1 is required.`);
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};
